import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDate, fmtDateTime } from "@/lib/format";
import { PageHeader, SectionCard, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge, ACCOUNT_LABEL } from "@/pages/admin/AdminCompanies";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Undo2, Trash2, AlertTriangle } from "lucide-react";

/**
 * Silinmişler (soft delete) listesi.  Buradan admin firmayı Arşiv'e geri
 * yükleyebilir veya Kalıcı Silme'yi deneyebilir.  Kalıcı silme henüz
 * uygulanmamıştır: backend 501 döner ve kullanıcıya bilgi mesajı
 * gösterilir.  Firma verileri korunur.
 */
export default function AdminDeleted() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoreRow, setRestoreRow] = useState(null);
  const [permRow, setPermRow] = useState(null); // step 1 confirm
  const [permStep2, setPermStep2] = useState(null); // step 2 with typed name
  const [permTypedName, setPermTypedName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/companies", {
        params: { filter: "deleted" },
      });
      setRows(data || []);
    } catch {
      toast.error("Silinmişler listesi yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runRestore = async () => {
    if (!restoreRow) return;
    setBusy(true);
    try {
      await api.patch(`/admin/companies/${restoreRow.id}/restore`, {});
      toast.success("Firma Arşiv durumuna geri yüklendi.");
      setRestoreRow(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Geri yükleme başarısız oldu.");
    } finally {
      setBusy(false);
    }
  };

  const runPermanent = async () => {
    if (!permStep2) return;
    setBusy(true);
    try {
      await api.delete(`/admin/companies/${permStep2.id}/permanent`);
      toast.success("Firma kalıcı olarak silindi.");
      setPermStep2(null);
      setPermTypedName("");
      load();
    } catch (e) {
      const status = e?.response?.status;
      if (status === 501) {
        toast.warning(
          e?.response?.data?.detail ||
            "Kalıcı silme henüz uygulanmadı. PayTR audit verilerinin korunması ve cascade delete analizi tamamlandıktan sonra aktifleştirilecektir.",
          { duration: 8000 },
        );
      } else {
        toast.error(e?.response?.data?.detail || "Kalıcı silme başarısız oldu.");
      }
      setPermStep2(null);
      setPermTypedName("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-deleted-page">
      <PageHeader
        title="Silinmişler"
        subtitle="Soft delete edilmiş firmalar. Firma verileri hâlâ korunur; Arşiv'e geri yüklenebilir veya kalıcı silme değerlendirilebilir."
      />

      <SectionCard>
        {loading ? (
          <div className="p-5 text-sm text-slate-500">Yükleniyor…</div>
        ) : rows.length === 0 ? (
          <EmptyState title="Silinmiş firma bulunmuyor" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/50">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-semibold">Firma</th>
                  <th className="px-5 py-3 font-semibold">Yetkili</th>
                  <th className="px-5 py-3 font-semibold">E-posta</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Arşive Alınma</th>
                  <th className="px-5 py-3 font-semibold whitespace-nowrap">Silinme</th>
                  <th className="px-5 py-3 font-semibold">Durum</th>
                  <th className="px-5 py-3 font-semibold text-right">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr
                    key={r.id}
                    className="hover:bg-slate-50/60"
                    data-testid={`admin-deleted-row-${r.id}`}
                  >
                    <td
                      className="px-5 py-3 cursor-pointer"
                      onClick={() => navigate(`/admin/companies/${r.id}`)}
                    >
                      <div className="font-medium text-slate-900">{r.name || "—"}</div>
                      <div className="text-xs text-slate-500">{r.owner?.username || ""}</div>
                    </td>
                    <td className="px-5 py-3">{r.owner?.full_name || "—"}</td>
                    <td className="px-5 py-3 text-slate-700 truncate max-w-[220px]">
                      {r.owner?.email || "—"}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">
                      {r.archived_at ? fmtDateTime(r.archived_at) : "—"}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-slate-700">
                      {r.deleted_at ? fmtDateTime(r.deleted_at) : "—"}
                    </td>
                    <td className="px-5 py-3">
                      <Badge tone={ACCOUNT_LABEL[r.account_status]} />
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="mr-2 rounded-lg"
                        onClick={() => setRestoreRow(r)}
                        data-testid={`admin-deleted-restore-${r.id}`}
                      >
                        <Undo2 className="w-4 h-4 mr-1" /> Arşiv&apos;e Geri Yükle
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="rounded-lg text-rose-700 border-rose-200 hover:bg-rose-50"
                        onClick={() => setPermRow(r)}
                        data-testid={`admin-deleted-permanent-${r.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" /> Kalıcı Olarak Sil
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Restore confirmation */}
      <AlertDialog open={!!restoreRow} onOpenChange={(v) => !v && setRestoreRow(null)}>
        <AlertDialogContent data-testid="admin-deleted-restore-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Firmayı Arşiv&apos;e geri yükle</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium">{restoreRow?.name}</span> firması Silinmişler bölümünden çıkarılıp <b>Arşiv</b> durumuna alınacak. Silinme tarihi temizlenir; arşiv tarihi ve firma verileri korunur.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-deleted-restore-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={runRestore}
              disabled={busy}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="admin-deleted-restore-ok"
            >
              {busy ? "İşleniyor…" : "Geri Yükle"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent delete — step 1 */}
      <AlertDialog open={!!permRow} onOpenChange={(v) => !v && setPermRow(null)}>
        <AlertDialogContent data-testid="admin-deleted-permanent-confirm-1">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Kalıcı silme — 1. Onay
            </AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium">{permRow?.name}</span> firması ve bağlı tüm veriler (müşteriler, tedarikçiler, işler, tahsilatlar, satın almalar, giderler, kasa hareketleri, kategoriler, kullanıcı) <b>geri dönüşü olmayacak şekilde</b> silinmek üzere.
              <br /><br />
              <b>Not:</b> Kalıcı silme şu an aktif değildir. PayTR ödeme audit verilerinin korunması gerektiğinden endpoint henüz devrede olmayabilir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-deleted-permanent-cancel-1">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setPermStep2(permRow); setPermRow(null); setPermTypedName(""); }}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="admin-deleted-permanent-next"
            >
              Devam
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Permanent delete — step 2 (typed confirmation) */}
      <AlertDialog open={!!permStep2} onOpenChange={(v) => { if (!v) { setPermStep2(null); setPermTypedName(""); } }}>
        <AlertDialogContent data-testid="admin-deleted-permanent-confirm-2">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Kalıcı silme — 2. Onay
            </AlertDialogTitle>
            <AlertDialogDescription>
              Onaylamak için firma adını aynen yazın:
              <br />
              <span className="font-semibold text-slate-900">{permStep2?.name}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="pt-2">
            <Input
              value={permTypedName}
              onChange={(e) => setPermTypedName(e.target.value)}
              placeholder="Firma adını yazın"
              className="h-10 rounded-lg"
              data-testid="admin-deleted-permanent-typed-name"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-deleted-permanent-cancel-2">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={runPermanent}
              disabled={busy || (permTypedName || "").trim() !== (permStep2?.name || "").trim() || !permStep2?.name}
              className="bg-rose-600 hover:bg-rose-700 disabled:opacity-40"
              data-testid="admin-deleted-permanent-confirm-ok"
            >
              {busy ? "İşleniyor…" : "Kalıcı Olarak Sil"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
